import type { SceneGroup } from '@lumen5/luminary';

import { $matchBoundaryNodes } from './lexical-boundary';

import { getEditorFromSceneGroupId } from 'js/src/data/transcript';
import { $getTimeIndicatedBy } from 'js/src/data/transcript/transcript-operations';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { getVoiceDrivenBaseAssetCutRegions } from 'js/src/luminary/luminary-selectors';

export type SplitBoundary = { splitMillis: number } | { error: string };

/**
 * Resolve the transcript-time point at which to split a voicedriven scene group, given the
 * sequence of words to split AFTER (the split lands immediately after the last of them).
 *
 * Uses the `endTime` branch of `$getTimeIndicatedBy` — the same lexical helper the transcript's
 * "Split after selection" button drives (SplitTranscriptPlugin) — so a word-driven split lands
 * exactly where a user's would: snapping to a scene boundary or a pre-existing cut that falls
 * between the two words, otherwise the midpoint between them. Reads the scene group's headless
 * Lexical editor (words + `<pause>` SilenceNodes), so a split boundary can reference or land on a
 * pause (include the `<pause seconds="N"/>` tag in the words).
 *
 * Returns `{ error }` (message without an "Error:" prefix) on any problem.
 */
export const resolveSplitBoundaryOperation = createLuminaryOperation(
    ({ getState, call }, sceneGroup: SceneGroup, requestedWords: string[]): SplitBoundary => {
        if (requestedWords.length === 0) {
            return { error: 'words array cannot be empty. Please provide at least one word.' };
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) {
            return { error: 'The transcript editor for this scene group is not available.' };
        }

        // Cut-aware (see $isInCutRegion) so a re-split only resolves within the span this group still owns.
        const cutRegions = getVoiceDrivenBaseAssetCutRegions(sceneGroup);
        return editor.getEditorState().read((): SplitBoundary => {
            const nodeSpan = $matchBoundaryNodes(requestedWords, cutRegions);
            if ('error' in nodeSpan) {
                return nodeSpan;
            }
            if (!nodeSpan.hasFollowingToken) {
                return {
                    error: 'Cannot split after the last word of the transcript. Choose a word sequence that has at least one word after it.',
                };
            }
            const { endTime } = call(
                $getTimeIndicatedBy,
                nodeSpan.firstNode,
                nodeSpan.lastNode,
                sceneGroup.id,
            );
            return { splitMillis: endTime };
        });
    },
);
